import React from 'react';
import styled, { css } from 'styled-components';

import { Box, Heading } from '@rover/kibble/core';
import { Props as BoxProps } from '@rover/kibble/core/Box/Box';
import useTheme from '@rover/kibble/themes/useTheme';

import type { CardStyleOptions } from './Card.constants';
import getCardStyle from './Card.constants';

interface CardLinkProps {
  'aria-label': string;
  href: string;
}
/**
 * Use this if you are needing the card to have a clickable link that covers the entire card
 *
 * 📌 If you want to mark up the link for Schema, be sure to include a meta tag above the <CardLink>.
 *
 * ie;
 * ```
 * <meta itemProp="url" content={webUrl} />
 * <CardLink href={webUrl} aria-label={ariaLabel} />
 * ```
 * Refer to CarouselSitterCard or CarouselReviewCard for an active example
 */
export const CardLink = styled.a.attrs<CardLinkProps>(({ 'aria-label': ariaLabel, href }) => ({
  'aria-label': ariaLabel,
  href,
}))<CardLinkProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  cursor: pointer;
`;

export type Props = BoxProps & {
  cardStyle?: CardStyleOptions;
  footer?: React.ReactNode;
  isStacked?: boolean;
  hasHr?: boolean;
};

type CardWrapperProps = {
  isStacked?: boolean;
};

// we need to use a styled-component here for the pseudo selectors
const CardWrapper = styled(Box)<CardWrapperProps>`
  ${(props) =>
    props.isStacked &&
    css`
      &:not(:first-of-type) {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }

      &:not(:last-of-type) {
        border-bottom-width: 0;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    `};
`;

const Card = ({
  hasHr = false,
  footer = false,
  cardStyle = 'default',
  isStacked = false,
  children,
  ...boxProps
}: Props): React.ReactElement => {
  const theme = useTheme();
  return (
    <CardWrapper
      data-testid="card-wrapper"
      width="100%"
      borderRadius="4px"
      padding="6x"
      position="relative"
      isStacked={isStacked}
      {...getCardStyle(theme, cardStyle)}
      {...boxProps}
    >
      {hasHr && (
        <Box
          webAs="hr"
          data-testid="card-hr"
          width="70px"
          mb="5x"
          border="0 none"
          borderBottom={`3px solid ${theme.colors.green['600']}`}
        />
      )}
      {children}
      {footer && (
        <Box
          data-testid="card-footer"
          textAlign="right"
          position={boxProps.height ? 'absolute' : 'static'}
          right={boxProps.padding || '6x'}
          bottom={boxProps.padding || '6x'}
        >
          {/* We're using a Heading here to match the previous design's 26px text */}
          <Heading
            webProps={{ as: 'span', responsive: false }}
            size="400"
            fontWeight="regular"
            textColor="tertiary"
          >
            {footer}
          </Heading>
        </Box>
      )}
    </CardWrapper>
  );
};

export default Card;
